import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SERVICES, getServiceBySlug } from "@/data/services";
import PageShell from "@/components/PageShell";
import Lightbox from "@/components/Lightbox";
import { usePortfolioImages } from "@/hooks/usePortfolioImages";
import { useSEO } from "@/hooks/useSEO";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const { t, lang } = useLanguage();
  const { images: uploaded, media: uploadedMedia, cover: uploadedCover, loading } = usePortfolioImages(slug);
  // Lightbox state — null = closed, integer = index in `galleryItems`.
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useSEO({
    title: service ? t(`services.${service.key}.label`) : "Service",
    description: service ? t(`services.${service.key}.description`) : "",
    image: service?.cover,
    lang,
  });

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const label = t(`services.${service.key}.label`);
  const tagline = t(`services.${service.key}.tagline`);
  const description = t(`services.${service.key}.description`);
  const highlights = t(`services.${service.key}.highlights`);

  // Mixed gallery: use uploaded media when ≥ 2 items, else default images
  const galleryItems =
    uploadedMedia.length >= 2
      ? uploadedMedia
      : service.gallery.map((u) => ({
          id: u,
          url: u,
          media_type: "image",
          mime_type: null,
        }));
  // Hero photo strategy:
  //   - If admin has chosen a custom cover → use it instantly (instant from
  //     cache on subsequent visits)
  //   - Else, on first-ever visit: wait for the API to confirm there's no
  //     custom cover before falling back to the static SERVICES cover. This
  //     avoids the visible swap from static-photo to admin-photo on the very
  //     first page load.
  const heroImage = uploadedCover || (loading ? null : service.cover);

  const others = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  // When a service ships with a live embed (e.g. Photothèque Event), we keep
  // the standard hero photo but strip everything below the embed — portfolio
  // gallery, CTA band and related-services strip are hidden so the page ends
  // on the live iframe.
  const hasEmbed = Boolean(service.embed_url);

  return (
    <PageShell dataTestId={`service-detail-${slug}`}>
      {/* Hero */}
      <section
        data-testid="service-detail-hero"
        data-on-dark="true"
        className="relative h-[70vh] md:h-[85vh] overflow-hidden"
      >
        {heroImage && (
          <img
            src={heroImage}
            alt={label}
            className="absolute inset-0 w-full h-full object-cover animate-fade-in"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/30 to-[#050505]" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-16 md:pb-24">
          <Link
            to="/services"
            data-testid="service-back-link"
            className="kw-btn-ghost mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.25} />
            {t("detail.back")}
          </Link>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="overline kw-accent-line"
          >
            {t("nav.services")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[1] text-[#F2EBE5]"
          >
            {label}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-6 text-lg md:text-xl text-[#C5A059] italic font-serif-display"
          >
            {tagline}
          </motion.p>
        </div>
      </section>

      {/* Description + Highlights */}
      <section className="relative py-24 md:py-32 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <p className="overline kw-accent-line">{t("detail.overview")}</p>
            <h2 className="mt-6 font-serif-display text-3xl md:text-4xl leading-tight">
              {tagline}
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-base md:text-lg text-[#A19E9C] leading-relaxed">
              {description}
            </p>
            <div className="mt-12">
              <p className="overline">{t("detail.approach")}</p>
              <ul className="mt-6 space-y-4">
                {Array.isArray(highlights) &&
                  highlights.map((h, i) => (
                    <motion.li
                      key={h}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.05 }}
                      className="flex items-start gap-4"
                    >
                      <Check
                        className="w-4 h-4 text-[#C5A059] mt-1.5 flex-shrink-0"
                        strokeWidth={1.5}
                      />
                      <span className="text-[#F2EBE5] text-base md:text-lg font-light">
                        {h}
                      </span>
                    </motion.li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
         OPTIONAL: "How it works" + embedded live gallery.
         Rendered only for services that ship with an embed_url
         (Photothèque Event today). Anything below the embed is
         intentionally omitted — the page ends with the iframe.
         ============================================================ */}
      {hasEmbed && (
        <>
          {/* How it works — 3 flat translation slots (step_N_label/body) so the
              admin Content panel can edit each text individually. */}
          <section
            data-testid={`service-howto-${slug}`}
            className="relative py-20 md:py-28 bg-[#0a0a0a] border-y border-[#C5A059]/15"
          >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
              <p className="overline kw-accent-line">
                {t(`services.${service.key}.how_overline`)}
              </p>
              <h2 className="mt-4 font-serif-display text-3xl md:text-5xl leading-tight max-w-3xl">
                {t(`services.${service.key}.how_title`)}
              </h2>
              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                {[1, 2, 3].map((n, i) => (
                  <motion.div
                    key={n}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className="border-l-2 border-[#C5A059]/40 pl-5"
                  >
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059]">
                      {t(`services.${service.key}.how_step_${n}_label`)}
                    </p>
                    <p className="mt-4 text-base md:text-lg text-[#F2EBE5]/90 leading-relaxed font-light">
                      {t(`services.${service.key}.how_step_${n}_body`)}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Embedded live gallery — final block of the page. */}
          <section
            data-testid={`service-embed-${slug}`}
            className="relative py-20 md:py-28 max-w-[1400px] mx-auto px-6 md:px-12"
          >
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end mb-10">
              <div className="md:col-span-7">
                <p className="overline kw-accent-line">
                  {t(`services.${service.key}.embed_overline`)}
                </p>
                <h2 className="mt-4 font-serif-display text-3xl md:text-5xl leading-tight">
                  {t(`services.${service.key}.embed_title`)}
                </h2>
              </div>
              <div className="md:col-span-5">
                <p className="text-sm md:text-base text-[#A19E9C] leading-relaxed">
                  {t(`services.${service.key}.embed_intro`)}
                </p>
              </div>
            </div>

            <div
              className="border border-[#C5A059]/25 bg-[#0a0a0a] p-2 md:p-3 shadow-[0_30px_80px_-30px_rgba(197,160,89,0.25)]"
              data-testid={`service-embed-frame-${slug}`}
            >
              {/* "Powered by" banner — sits flush above the iframe so every
                  selfie-search session shows the source studio without
                  competing with the gallery UI. The right side carries a
                  scannable QR code so visitors viewing the gallery on a TV,
                  laptop or kakémono can instantly open it on their phone. */}
              <div
                data-testid={`service-embed-poweredby-${slug}`}
                className="flex items-center justify-between gap-4 px-4 md:px-5 py-3 border-b border-[#C5A059]/20 bg-[#050505]"
              >
                <a
                  href="https://kiwamistudios.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 min-w-0"
                  data-testid={`service-embed-poweredby-link-${slug}`}
                >
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-[#A19E9C] whitespace-nowrap">
                    {lang === "fr" ? "Propulsé par" : "Powered by"}
                  </span>
                  <span className="font-serif-display text-base md:text-lg text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors truncate">
                    Kiwami Studios
                  </span>
                </a>

                {/* QR — encodes the current page URL so a phone can open the
                    gallery instantly. Rendered as SVG (crisp, ~1 KB). The
                    border + cream padding makes scanning reliable under
                    venue lighting. */}
                <div className="flex items-center gap-3">
                  <span className="hidden md:inline-flex flex-col items-end text-right leading-tight">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
                      {lang === "fr" ? "Scannez-moi" : "Scan me"}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#A19E9C]">
                      {lang === "fr" ? "Ouvrir sur mobile" : "Open on mobile"}
                    </span>
                  </span>
                  <div
                    className="bg-[#F2EBE5] p-1.5 rounded-sm shadow-[0_4px_12px_-2px_rgba(197,160,89,0.35)]"
                    data-testid={`service-embed-qr-${slug}`}
                    aria-label={lang === "fr" ? "QR code de la galerie" : "Gallery QR code"}
                  >
                    <QRCodeSVG
                      value={typeof window !== "undefined" ? window.location.href.split("?")[0] : `https://kiwamistudios.com/services/${slug}`}
                      size={56}
                      level="M"
                      bgColor="#F2EBE5"
                      fgColor="#050505"
                      marginSize={0}
                    />
                  </div>
                </div>
              </div>

              <iframe
                src={service.embed_url}
                title={t(`services.${service.key}.embed_title`)}
                allow="camera; geolocation; clipboard-write"
                loading="lazy"
                className="block w-full bg-[#050505]"
                style={{ height: "800px", border: "0" }}
              />
            </div>
          </section>
        </>
      )}

      {/* The portfolio gallery, CTA band and "other services" strip are
          intentionally hidden for embed-driven services so the page ends
          on the live iframe. */}      {!hasEmbed && (
        <>
          {/* Portfolio Gallery */}
          <section
            data-testid="service-portfolio"
            className="relative py-24 md:py-32 max-w-[1400px] mx-auto px-6 md:px-12"
          >
            <p className="overline kw-accent-line mb-6">{t("detail.portfolio")}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl mb-14">
              {label}
            </h2>

            <div
              className="columns-2 md:columns-3 gap-3 md:gap-4 [column-fill:_balance]"
              data-testid="service-portfolio-grid"
            >
              {galleryItems.map((item, i) => {
                const isVideo = item.media_type === "video";
                return (
                  <motion.div
                    key={item.id || i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.6, delay: (i % 6) * 0.04 }}
                    onClick={() => setLightboxIndex(i)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setLightboxIndex(i);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="kw-img-frame kw-img-frame--natural mb-3 md:mb-4 break-inside-avoid block w-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]"
                    data-testid={`gallery-item-${slug}-${i}`}
                  >
                    {isVideo ? (
                      <video
                        src={item.url}
                        className="w-full pointer-events-none"
                        playsInline
                        preload="metadata"
                        muted
                      />
                    ) : (
                      <img
                        src={item.thumb_url || item.url}
                        alt={item.caption || `${label} ${i + 1}`}
                        className="w-full"
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="relative py-24 md:py-28 border-y border-[#C5A059]/20 bg-[#0a0a0a]">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-8">
                <h3 className="font-serif-display text-3xl md:text-5xl leading-tight">
                  {t("detail.request")}
                </h3>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link
                  to={`/contact?service=${service.slug}`}
                  data-testid={`service-cta-${slug}`}
                  className="kw-btn-primary"
                >
                  {t("nav.order")}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </section>

          {/* Other services */}
          <section className="relative py-24 md:py-28 max-w-[1400px] mx-auto px-6 md:px-12">
            <p className="overline kw-accent-line mb-6">
              {t("detail.other_services")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {others.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`other-service-${s.slug}`}
                  className="group kw-img-frame aspect-[5/6]"
                >
                  <img src={s.cover} alt={t(`services.${s.key}.label`)} />
                  <div className="absolute inset-0 z-10 flex items-end p-6">
                    <div>
                      <h4 className="font-serif-display text-2xl text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors">
                        {t(`services.${s.key}.label`)}
                      </h4>
                      <div className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#A19E9C] group-hover:text-[#C5A059] transition-colors">
                        {t("common.discover")}
                        <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Fullscreen lightbox — rendered via portal at body level so it sits
          above the navbar and any sticky element. Only mounts when an index
          is set; closing nulls it back out. */}
      <Lightbox
        items={galleryItems}
        index={lightboxIndex}
        serviceSlug={slug}
        onClose={() => setLightboxIndex(null)}
        onPrev={() =>
          setLightboxIndex((i) =>
            i === null ? null : (i - 1 + galleryItems.length) % galleryItems.length,
          )
        }
        onNext={() =>
          setLightboxIndex((i) =>
            i === null ? null : (i + 1) % galleryItems.length,
          )
        }
      />
    </PageShell>
  );
};

export default ServiceDetail;
